import { useEffect, useRef, useState } from 'react'

import Section from '@/components/ui/atoms/Section'
import { CardCarousel } from '@/components/ui/molecules/card-carousel'

import ProductsListItem from '@/components/core/products/products.list.item'
import ExclusiveGroupBanner from '@/components/core/shared/exclusive.group.banner'
import KnowUsBanner from '@/components/core/shared/know.us.banner'
import SubscriptionBanner from '@/components/core/shared/subscription.banner'
import { Button } from '@/components/ui/atoms/button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/atoms/card'
import VideoPlaceholder from '@/components/ui/molecules/video.placeholder'
import LeadAuthenticationDialog from '@/components/ui/organisms/lead-authentication-dialog'
import { cn, open } from '@/lib/client'
import { store as authentication_store } from '@/store/authentication'
import type { IAccount, IAuthentication } from '@/types/account'
import type { IFreeContent } from '@/types/free.content'
import type { IPaymentStandard } from '@/types/payment'
import type { IProduct } from '@/types/product'
import type { IImageResolution } from '@/types/static'
import { useStore } from '@nanostores/react'
import { Download } from 'lucide-react'
import FreeContentDetailsAditional from './free.content.details.aditional'

export default function FreeContentDetailsContent({
  content,
  payment,
}: {
  content: IFreeContent
  payment: IPaymentStandard
}) {
  const contentRef = useRef<HTMLElement | null>(null)
  const relatedRef = useRef<HTMLElement | null>(null)

  const $authentication = useStore(authentication_store.authentication)

  const [user, setUser] = useState<(IAccount & IAuthentication) | null>(null)

  useEffect(() => {
    setUser($authentication)
  }, [$authentication])

  const slideContent = {
    classes: (c: {
      _id: string
      url: string
      images: IImageResolution
    }) => {
      return (
        <div className="pb-6 px-2">
          <VideoPlaceholder
            url={c.url}
            title={c._id}
            img={c.images.small.url}
            width={380}
            height={216}
            className="rounded-2xl"
          />
        </div>
      )
    },
    courses: (product: IProduct) => {
      return (
        <div className="pb-6 px-2">
          <ProductsListItem product={product} payment={payment} />
        </div>
      )
    },
  }

  const handleClick = {
    course: () => {
      relatedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    download: (url: string) => open(url),
  }

  return (
    <Section id="free-content-details">
      <section
        className="flex justify-between gap-x-4 lg:gap-x-8 flex-col-reverse md:flex-row"
        ref={contentRef}
      >
        <div className="prose lg:prose-lg max-w-[40ch] lg:max-w-[70ch] prose-zinc">
          {content.highlight.title && <h2>{content.highlight.title}</h2>}
          <div
            className="mt-5"
            dangerouslySetInnerHTML={{
              __html: content.highlight.description || '',
            }}
          />
          <VideoPlaceholder
            url={content.highlight.media.url}
            title={content.highlight.title}
            img={content.highlight.media.images.large.url}
            width={content.highlight.media.images.large.width}
            height={content.highlight.media.images.large.height}
            className="rounded-2xl"
          />

          <section className="p-5 bg-zinc-100 rounded-2xl">
            {content.observation.title && <h2>{content.observation.title}</h2>}
            <div
              className="mt-5"
              dangerouslySetInnerHTML={{
                __html: content.observation.description || '',
              }}
            />
          </section>

          <ExclusiveGroupBanner
            className="not-prose"
            title={content.highlight.group.title}
            description={content.highlight.group.description}
            button={content.highlight.group.button}
            link={content.permanentLink}
          />

          <section className="scroll-mt-24" ref={relatedRef}>
            {content.classes.title && (
              <h2 className="font-bold text-3xl mt-7">
                {content.classes.title}
              </h2>
            )}
            <CardCarousel
              elements={content.classes.list}
              id="_id"
              slideContent={slideContent.classes}
              className="md:basis-1/1 lg:basis-1/2 xl:basis-1/2"
            />
          </section>

          {content.exams?.list?.some((t) => t.url) && (
            <section className="py-5">
              {content.exams.title && <h2>{content.exams.title}</h2>}
              <div
                className="mt-5"
                dangerouslySetInnerHTML={{
                  __html: content.exams.description || '',
                }}
              />
              <div className="grid lg:grid-cols-2 gap-2 not-prose">
                {content.exams.list.map((c) => (
                  <Card
                    key={c._id}
                    className="bg-white border rounded-lg basis-1/2"
                  >
                    <CardHeader>
                      <img
                        loading="lazy"
                        src={c.image}
                        alt={c.title}
                        className="rounded-md"
                        width={380}
                        height={165}
                      />
                    </CardHeader>
                    <CardContent className="lg:min-h-32">
                      <h2 className="font-semibold text-gray-900 text-lg">
                        {c.title}
                      </h2>
                      <h3 className="text-gray-400 line-clamp-2 text-lg font-medium">
                        {c.questions}
                      </h3>
                    </CardContent>
                    <CardFooter>
                      <Button
                        className={cn(
                          'bg-sky-500 w-full hover:bg-sky-600 hover:text-white',
                        )}
                        asChild
                      >
                        <a
                          href={c.url}
                          className={cn('flex items-center')}
                          rel="noreferrer"
                          target="_blank"
                        >
                          <Download className="mr-2" size={18} />
                          {c.button}
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </section>
          )}

          <SubscriptionBanner className="not-prose" />

          {content.ebooks?.list?.some((t) => t.url) && (
            <section className="py-5">
              {content.ebooks.title && <h2>{content.ebooks.title}</h2>}
              <div className="grid gap-2 not-prose">
                {content.ebooks.list.map((c) => (
                  <Card
                    key={c._id}
                    className="bg-white flex flex-col lg:flex-row items-center border rounded-lg"
                  >
                    <CardHeader>
                      <img
                        loading="lazy"
                        src={c.image}
                        alt={c.title}
                        className="rounded-md"
                        width={230}
                        height={325}
                      />
                    </CardHeader>
                    <CardContent className="p-6 items-center justify-center space-y-4">
                      <h2 className="font-semibold text-gray-900 text-2xl">
                        {c.title}
                      </h2>
                      <h3 className="text-gray-400 line-clamp-2 text-lg font-medium">
                        {c.description}
                      </h3>
                      {user?._id ? (
                        <Button
                          className="bg-sky-500 hover:bg-sky-600 hover:text-white"
                          size={'lg'}
                          onClick={() => handleClick.download(c.url)}
                        >
                          <Download className="mr-2" size={18} />
                          {c.button}
                        </Button>
                      ) : (
                        <LeadAuthenticationDialog
                          title="Olá, estudante"
                          description="Adquira já seu conteúdo gratuito"
                          cta="Acessar conteúdo"
                          name={c.title}
                          callbackUrl={c.url}
                        >
                          <Button size={'lg'}>
                            <Download className="mr-2" size={18} />
                            {c.button}
                          </Button>
                        </LeadAuthenticationDialog>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="sm:h-screen sm:-mt-48 sm:sticky sm:top-24 mb-6">
          <FreeContentDetailsAditional
            content={content}
            handleClick={handleClick.course}
          />
        </aside>
      </section>
      <KnowUsBanner className="not-prose" handleClick={handleClick.course} />
      <section className="scroll-mt-24" ref={relatedRef}>
        <h2 className="font-bold text-3xl mt-7">Confira os nossos cursos</h2>
        <CardCarousel
          elements={content.courses}
          id="_id"
          slideContent={slideContent.courses}
        />
      </section>
    </Section>
  )
}
